import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

// Import types
import type { LastPaymentsRedirectUrl } from 'types/api/payments';

// Import helpers
import { cancelTransaction } from 'helpers/payments';
import { openNotification } from 'components/utilities/notification/notification';
import { sendRetryPaymentEvent, sendCancelPaymentEvent } from 'helpers/tracking';

// Import hooks
import { useAppSelector } from 'store/hooks';

export const useLastTransaction = (payment: LastPaymentsRedirectUrl | null, type: 'packet' | 'product') => {
	const client = useQueryClient();
	const { t } = useTranslation('transaction');
	const [isLoading, setIsLoading] = useState(false);
	const token = useAppSelector(({ subscriber }) => subscriber.token);

	const title = payment?.title.replace(/;/g, ',');

	const isFailed = payment?.status === 'failed';

	const onRetry = () => {
		if (!payment?.redirect_url) return;
		setIsLoading(true);

		sendRetryPaymentEvent({ item_name: payment.title, item_category: 'Pakiet' });
		window.location.replace(payment.redirect_url);
	};

	const onCancel = (callback?: () => void) => {
		if (!payment?.payment_id) return;
		setIsLoading(true);

		cancelTransaction(payment.payment_id)
			.then(() => {
				openNotification({
					type: 'success',
					title: t(`cancel_transaction_${type}_success`, { title })
				});
				client.invalidateQueries(['last_payments', token]);
				client.invalidateQueries(['current_subscription', token]);

				sendCancelPaymentEvent({ item_name: payment.title, item_category: 'Pakiet' });

				callback?.();
			})
			.catch(() => {
				openNotification({
					type: 'error',
					title: t('notification_error_title'),
					description: t(`cancel_transaction_${type}_error`, { title })
				});
			})
			.finally(() => setIsLoading(false));
	};

	return { title, isFailed, isLoading, onCancel, onRetry };
};
